/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { orderBy } from 'lodash/collection';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';

import { AdminService } from '../../services/admin.service';
import { AdminNBClassifierService } from '../../services/admin/admin-nbclassifier.service';
import { AdminClassifierService } from '../../services/admin/admin-classifier.service';
import { NerService } from '../../services/ner.service';
import { MatchesService } from '../../services/matches.service';
import { ParserService } from '../../services/parser.service';
import { CacheService } from '../../global/ng2-cache/services/cache.service';
import { SessionService } from '../../services/session2.service';
import { ErrorService } from '../../services/error.service';
import { GlobalMessageService } from '../../services/global-message.service';
import { AdminOpsService } from '../../services/admin/admin-ops.service';

import { Constants, EmailType } from '../../global/constants';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';
import { BsModalService } from 'ngx-bootstrap/modal/bs-modal.service';

@Component({
  selector: 'app-admin-email-detail',
  styleUrls: ['./admin-email-detail.component.scss'],
  templateUrl: './admin-email-detail.component.html'
})
export class AdminEmailDetailComponent implements OnInit {

  goldensetDialogRef: BsModalRef;
  goldensetType: string;
  goldensetNotes: string = '';

  id: string;
  tab: any = 'classify';

  // for email detail and raw output
  ed: any = {};
  ce: any = {};
  raw: any = {};
  recordDisplay: any = {
    ed: false,
    ce: false,
    raw: false,
    matches: [false]
  };

  senderContact: any = {};
  clean: string = '';
  showHighlight = true;
  highlight: any;
  nohighlight: any;

  // for classification
  testResults: any = {};
  classifyContent: any;
  subjectContent: any;
  spamBodyContent: any;

  // for custom classifiers
  customResults: any[] = [];

  // for ner
  nerResults: any = {};
  nerContent: any;

  // for boundary
  boundaryResults: any = {};
  boundaryAnnotated: string;
  boundaryContent: any;

  // for raw
  dba: any = {};

  // for matches dry-run
  matchesResult: any = {};

  loading: any = {
    ed: true,
    classify: true,
    custom: true,
    ner: true,
    boundary: true,
    matches: true
  };

  report: any = {
    incorrectType: false,
    missingName: false,
    missingOther: false,
    invalidName: false,
    badAssociation: false,
    comments: ''
  };

  constructor(private route: ActivatedRoute,
              private domSanitizer: DomSanitizer,
              private cacheService: CacheService,
              private sessionService: SessionService,
              private errorService: ErrorService,
              private adminNBClassifierService: AdminNBClassifierService,
              private adminClassifierService: AdminClassifierService,
              private nerService: NerService,
              private matchesService: MatchesService,
              private parserService: ParserService,
              private adminService: AdminService,
              private globalMessageService: GlobalMessageService,
              private adminOpsService: AdminOpsService,
              private modalService: BsModalService,
              private constants: Constants) { }

  ngOnInit() {
    this.route.params.subscribe(
      (params) => {
        this.id = params.id;
        if (!this.cacheService.get('session')) {
          this.sessionService.getSession(true).subscribe(
            () => {
              this.initView();
            }
          );
        }
        else {
          this.initView();
        }
      }
    );
  }

  getTypeClass(type: string) {
    let cssClass: string = 'category-3';
    const et: EmailType =
      this.constants.emailTypes.find((emt) => {
        return emt.display == type;
      });
    if (et) {
      cssClass = et.cssClass;
    }
    return cssClass;
  }

  addCustomerHighlights() {
    this.highlight = this.clean;
    // sort people by length of title. This is to make sure that supersets of titles get highlighted
    // correctly. Otherwise "Assistant" gets highlighted twice when there's also "Assistant Director of Marketing"
    this.ed.contacts.sort((a, b) => {
      if (a && b) {
        if (a.title && a.title.length > b.title.length) {
          return -1;
        }
        else {
          return 1;
        }
      }
      else {
        return -1;
      }
    });

    this.ed.parsed_dates.forEach((d) => {
      const dRE = new RegExp(d.original.replace(/[+.()]/g, '\\$&'), 'gi');
      this.highlight = this.highlight.replace(dRE, '<span class="h-date">' + d.original + '</span>');
    });

    this.ed.contacts.forEach((p) => {
      if (p) {
        if (!p.sender) {
          const safeEmail= p.email_address.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // escape special characters for regex
          const eRE = new RegExp(safeEmail, 'gi');
          this.highlight = this.highlight.replace(eRE, '<span class="h-email">' + p.email_address + '</span>');

          if (p.name.indexOf('@') < 0) { // otherwise email alias is overwritten by name
            const escapeName = (p.name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const nRE = new RegExp(escapeName, 'gi');
            this.highlight = this.highlight.replace(nRE, '<span class="h-name">' + p.name + '</span>');
          }

          if (p.phone_number && p.phone_number.length > 0) {
            const pRE = new RegExp(p.phone_number.replace(/[+.()]/g, '\\$&'), 'gi');
            // var pRE = new RegExp(p.phone_number, 'gi');
            this.highlight = this.highlight.replace(pRE, '<span class="h-phone">' + p.phone_number + '</span>');
          }

          if (p.title && p.title.length > 0) {
            const safeTitle = (p.title).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const tRE = new RegExp(safeTitle, 'gi');
            this.highlight = this.highlight.replace(tRE, '<span class="h-title">' + p.title + '</span>');
          }
        }
      }
    });
    return this.highlight;
  }

  addClassifyHighlights() {
    this.classifyContent = this.clean;
    let wonCategory: any;
    this.testResults.classify_data.forEach((category) => {
      if (category.c == this.testResults.type) {
        wonCategory = category;
      }
    });

    if (wonCategory) {
      wonCategory.w.forEach((w) => {
        const wRE = new RegExp(`\\b${(w.w).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        this.classifyContent = this.classifyContent.replace(wRE, '<span class="h-word">' + w.w + '</span>');
      });
    }

    this.spamBodyContent = this.clean;
    this.testResults.spam_body[0].w.forEach((w) => {
      const wRE = new RegExp(`\\b${(w.w).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      this.spamBodyContent = this.spamBodyContent.replace(wRE, '<span class="h-word">' + w.w + '</span>');
    });
  }

  addCustomClassifyHighlights(content: string, testResults: any[]) {
    let formatted: string = content;
    if (testResults.length > 0) {
      testResults[0].w.forEach((w) => {
        const wRE = new RegExp(`\\b${(w.w).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        formatted = formatted.replace(wRE, '<span class="h-word">' + w.w + '</span>');
      });
    }
    return formatted;
  }

  addSubjectHighlights() {
    this.subjectContent = this.ce.subject;
    this.testResults.subject_type.forEach((sr) => {
      const escapeContent = (sr.content).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const srRE = new RegExp(`${escapeContent}`, 'i');
      this.subjectContent = this.subjectContent.replace(srRE, '<span class="h-subjectword">' + sr.content + '</span>');
    });
  }

  addNerHighlights() {
    this.nerContent = this.clean;
    this.nerResults.names.forEach((w) => {
      const safeName=(w.name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // escape special characters for regex
      const wRE = new RegExp(`\\b${safeName}\\b`, 'i');
      this.nerContent = this.nerContent.replace(wRE, '<span class="h-word">' + w.name + '</span>');
    });
  }

  addBoundaryHighlights() {
    this.boundaryContent = this.cleanHtml(this.boundaryAnnotated);
    this.boundaryContent = this.boundaryContent
      .replace('{content}', '<div class="h-content">')
      .replace('{/content}', '</div>')
      .replace('{original}', '<div class="h-original">')
      .replace('{/original}', '</div>')
      .replace('{footer}', '<div class="h-footer">')
      .replace('{/footer}', '</div>')
      .replace(/{signature}/g, '<div class="h-signature">')
      .replace(/{\/signature}/g, '</div>');
  }

  refreshClassify() {
    this.classifyTest();
    this.customClassifyTest();
  }

  refreshNer() {
    this.nerTest();
  }

  getObfuscated() {
// tslint:disable-next-line: no-console
    this.adminOpsService.getObfuscatedEmail(this.id).toPromise().then((result) => console.log(`db.raw_email.insert(${result})`));
  }

  markGolden(dialog) {
    this.goldensetDialogRef = this.modalService.show(dialog, { class: 'modal-md' });
  }

  markGoldenConfirm() {
    this.goldensetDialogRef.hide();
    this.adminOpsService.addToGoldenset(this.id, this.goldensetType, this.goldensetNotes).subscribe(
      () => {
        this.globalMessageService.message('Email added to golden set');
      },
      (error) => {
        this.globalMessageService.error('Failed to add to the goldenset.');
        this.errorService.log(error, 'admin-email-detail.component', 'goldenset');
      }
    );
  }

  getRawHeaders() {
    const ret: any[] = [];
    for (let key of this.raw.headers) {
      ret.push({
        k: key,
        v: typeof this.raw.headers[key] == 'object' ? JSON.stringify(this.raw.headers[key]) : this.raw.headers[key]
      });
    }
    return ret;
  }

  private cleanHtml(content: string) {
    const markup = content
      .trim()
      .replace(/<mailto:(.{0,25}[@]).{0,40}>/g, ' ')  // these are hidden from parsing, hide them from the interface
      .replace(/[<>]+/g, ' ')                         // emails are sometimes hidden between these, dump them
      .replace(/\n/g, '<br />')                       // replace line feeds with break tags to preserve format for display
      .replace(/[\s]+/g, ' ');                        // replace all whitespace with a single space
    return markup + '<br/><br/>';
  }

  private initView() {
    this.loading = {
      ed: true,
      classify: true,
      ner: true
    };
    this.adminService.getEmailDetail(this.id).subscribe(
      (result: any) => {

        this.ed = result.ed;
        this.ce = result.ce;
        this.raw = result.raw || null;

        // classify
        this.classifyTest();

        // custom classifiers
        this.customClassifyTest();

        // entity extraction
        this.nerTest();

        // boundary test
        this.boundaryTest();

        // dba stuff
        this.resetDBA();

        // matches dry-run
        this.matchesTest();

        this.ed.type = this.constants.emailTypes.find((ct) => {
          return ct.typeName == this.ed.type;
        }).display;

        this.ed.contacts.forEach((val, _) => {
          if (val) {
            val.phone_number = val.phone_number ? val.phone_number.length > 0 ? val.phone_number : 'n/a' : 'n/a';
            if (val.sender) {
              this.senderContact = val;
            }
          }
        });

        this.ed.parsed_dates.forEach((date) => {
          const utcDate = new Date(date.parsed);
          utcDate.setHours(utcDate.getHours() + 10);
          date.parsed = utcDate;
        });

        if (this.ed.body_start_index && this.ed.body_start_index >= 0) {
          this.ed.customer_email.text = this.ed.customer_email.text.substring(this.ed.body_start_index, this.ed.customer_email.text.length - 1);
        }

        this.clean = this.cleanHtml(this.ce.text);
        this.nohighlight = this.domSanitizer.bypassSecurityTrustHtml(this.clean);
        this.highlight = this.domSanitizer.bypassSecurityTrustHtml(this.addCustomerHighlights());
        this.loading.ed = false;
      },
      (error) => {
        this.globalMessageService.error();
        this.errorService.log(error, 'admin-email-detail.component', 'initView');
      }
    );
  }

  private classifyTest() {
    this.loading.classify = true;
    this.testResults = {};
    this.adminNBClassifierService.classify(this.ed._id).subscribe(
      (result: any) => {
        this.testResults = JSON.parse(result);
        this.goldensetType = this.testResults.type;
        this.testResults.subject_type.forEach((st) => {
          st.lang = this.constants.languages.find((l) => l.name == st.language).display;
          if (st.category == 'x') {
            st.category = 'n/a';
          }
        });

        this.addClassifyHighlights();
        this.addSubjectHighlights();
        this.loading.classify = false;
      },
      (error) => {
        this.globalMessageService.error();
        this.errorService.log(error, 'admin-email-detail.component', 'classifyTest');
      }
    );
  }

  private customClassifyTest() {
    this.loading.custom = true;
    this.customResults = [];
    this.adminClassifierService.getAllCustomClassifierResults(this.ce._id).subscribe(
      (result: any) => {
        this.customResults = result;
        this.customResults.forEach((cr) => {
          cr.classifyContent = this.addCustomClassifyHighlights(cr.content, cr.results.classify_data);
          // to make backwards compatible with barebones NB classifier
          cr.results.nb = {
            classify_data: cr.results.classify_data.nb
          };
          cr.results.phrase = {
            exact: cr.results.classify_data.phrase.exact.length > 0 ? cr.results.classify_data.phrase.exact[0].content : 'n/a',
            fuzzy: cr.results.classify_data.phrase.fuzzy.length > 0 ? cr.results.classify_data.phrase.fuzzy[0].content : 'n/a'
          }
        });
        this.loading.custom = false;
      },
      () => {
        this.globalMessageService.error();
      }
    );
  }

  private nerTest() {
    this.loading.ner = true;
    this.nerResults = {};
    this.nerService.extract(this.ed._id).subscribe(
      (result: any) => {
        this.nerResults = result.taxonomy;
        this.nerResults.signatureData = {
          names: [],
          email_addresses: [],
          phone_numbers: [],
          titles: []
        };

        result.taxonomySignatures.forEach((sig) => {
          sig.extract.names.forEach((name) => {
            this.nerResults.signatureData.names.push(name);
          });
          sig.extract.email_addresses.forEach((email) => {
            this.nerResults.signatureData.email_addresses.push(email);
          });
          sig.extract.phone_numbers.forEach((pn) => {
            this.nerResults.signatureData.phone_numbers.push(pn);
          });
          sig.extract.titles.forEach((title) => {
            this.nerResults.signatureData.titles.push(title);
          });
        });

        this.boundaryAnnotated = result.annotatedText;
        this.addNerHighlights();
        this.addBoundaryHighlights();
        this.loading.ner = false;
      },
      (error) => {
        this.globalMessageService.error();
        this.errorService.log(error, 'admin-email-detail.component', 'nerTest');
      }
    );
  }

  private resetDBA() {
    this.dba = {
      ed: `db.email_detail.find({_id: ObjectId('${this.ed._id}')}).pretty()`,
      ce: `db.customer_email.find({_id: ObjectId('${this.ce._id}')}).pretty()`,
      re: `db.raw_email.find({_id: ObjectId('${this.ce.raw_id}')}).pretty()`,
      c: `db.account_contact.find({account_id: ObjectId('${this.ed.account_id}'), email_detail: { $elemMatch: { ed_id: ObjectId('${this.ed._id}')}}}).pretty()`,
      wfq: `db.workflow_queue.find({account_id: ObjectId('${this.ed.account_id}'), emails: { $elemMatch: { ed_id: ObjectId('${this.ed._id}')}}}).pretty()`
    };
  }

  private boundaryTest() {
    this.loading.boundary = true;
    this.boundaryResults = {};
    this.parserService.boundary(this.ce.text).subscribe(
      (result: any) => {
        this.boundaryResults = result;
        // this.addBoundaryHighlights(); // now part of the ner results from the pipeline service
        this.loading.boundary = false;
      },
      (error) => {
        this.globalMessageService.error();
        this.errorService.log(error, 'admin-email-detail.component', 'boundaryTest');
      }
    );
  }

  refreshMatches() {
    this.matchesTest();
  }

  private matchesTest() {
    this.loading.matches = true;
    this.matchesResult = {};
    this.matchesService.dryRun(this.ed.account_id, this.ed._id).subscribe(
      (result: any) => {
        this.matchesResult = orderBy(result, ['is_match', 'name'], ['desc', 'asc']);
        this.recordDisplay.matches = new Array(this.matchesResult.length);
        this.recordDisplay.matches = Array.apply(null, {length: this.matchesResult.length}).map((e) => false);
        this.loading.matches = false;
      },
      (error) => {
        this.globalMessageService.error();
        this.errorService.log(error, 'admin-email-detail.component', 'matchesTest');
      }
    );
  }

  private initReport() {
    this.report = {
      incorrectType: false,
      missingName: false,
      missingOther: false,
      invalidName: false,
      badAssociation: false,
      comments: ''
    };
  }
}



// WEBPACK FOOTER //
// ./client/app/admin/admin-email-detail/admin-email-detail.component.ts